import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik, FieldArray, FormikProvider, Form } from "formik";
import * as Yup from "yup";

// Component
import InputField from "../../../component/input-field";
import Select from "react-select";
import swal from "@sweetalert/with-react";
import CustomeModal from "../../../component/modal-popup/customeModal";
import { sanitizeInput } from "../../../utils";
import "../../../css/theme.css";

// Redux
import { useDispatch, useSelector } from "react-redux";
import {
  getMasterUOMData,
  getAllSkuData,
} from "../../../redux/actions/skuMaster";
import {
  addProductData,
  updateProductData,
  getProductDataById,
} from "../../../redux/actions/productMaster";
import { RESET_SINGLE_PRODUCT_DATA } from "../../../redux/constants";

function CreateProduct() {
  const navigate = useNavigate();
  const state = useLocation().state;

  // Redux
  const dispatch = useDispatch();
  const { master_uom, allSkuData } = useSelector((state) => state.skuMaster);
  const { singleProduct } = useSelector((state) => state.productMaster);

  //State
  const [UOMOptions, setUOMOptions] = useState([]);
  const [skuOptions, setSkuOptions] = useState([]);
  const [loader, setLoader] = useState(false);

  // Function to GET UOM data
  const getUOMList = () => {
    const request = {
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };

    dispatch(getMasterUOMData(request));
  };

  // Function to ADD Product
  const addProduct = () => {
    setLoader(true);
    const request = {
      payload: formik.values,
      onSuccess: () => {
        setLoader(false);
        formik.resetForm();
        navigate("/product/overview");
      },
      onError: (error) => {
        setLoader(false);
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(addProductData(request));
  };

  // Funtion to UPDATE Product
  const updateProduct = () => {
    setLoader(true);
    const request = {
      product_id: state,
      payload: formik.values,
      onSuccess: () => {
        dispatch({ type: RESET_SINGLE_PRODUCT_DATA });
        formik.resetForm();
        setLoader(false);
        navigate("/product/overview");
      },
      onError: (error) => {
        setLoader(false);
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };

    dispatch(updateProductData(request));
  };

  // Funtion to GET SKU data
  const getSKUList = () => {
    const request = {
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getAllSkuData(request));
  };

  // Function to GET Product details by its id
  const getProductDetails = () => {
    const request = {
      product_id: state,
      onError: (error) => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content={error?.message}
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };
    dispatch(getProductDataById(request));
  };

  useEffect(() => {
    if (master_uom.length > 0) {
      setUOMOptions(
        master_uom.map((element) => {
          const obj = {
            label: element.master_uom_name,
            value: element.master_uom_id,
          };
          return obj;
        })
      );
    }
    if (allSkuData.length > 0) {
      setSkuOptions(
        allSkuData.map((element) => {
          const obj = {
            label: element.sku_name,
            value: element.sku_master_id,
          };
          return obj;
        })
      );
    }
  }, [master_uom, allSkuData]);

  useEffect(() => {
    getUOMList();
    getSKUList();
    if (state) {
      getProductDetails();
    }
  }, []);

  // Validation Schema
  const validationSchema = Yup.object().shape({
    product_name: Yup.string().required("This field is required"),
    product_no: Yup.string().nullable(),
    description: Yup.string().nullable(),
    min_qty: Yup.string().required("This field is required"),
    master_uom_id: Yup.string().required("This field is required"),
    sku: Yup.array().of(
      Yup.object().shape({
        sku_master_id: Yup.string().required("This field is required"),
        qty: Yup.string().required("This field is required"),
      })
    ),
  });

  // Formik
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      product_name: singleProduct ? singleProduct.product_name : "",
      product_no: singleProduct?.product_no ? singleProduct.product_no : "",
      description: singleProduct?.description ? singleProduct.description : "",
      min_qty: singleProduct ? singleProduct.min_qty : "",
      master_uom_id: singleProduct ? singleProduct.master_uom_id : "",
      sku:
        singleProduct && singleProduct.product_sku_mappings.length > 0
          ? singleProduct.product_sku_mappings.map((item) => {
              const obj = {
                sku_master_id: item.sku_master_id,
                qty: item.qty,
                product_sku_mapping_id: item.product_sku_mapping_id,
                product_master_id: item.product_master_id,
              };
              return obj;
            })
          : [
              {
                sku_master_id: "",
                qty: "",
              },
            ],
    },
    validationSchema,
    onSubmit: () => {
      state ? updateProduct() : addProduct();
    },
  });

  const getSkuUnit = (product_id) => {
    const sku = allSkuData.find((item) => item.sku_master_id === product_id);
    return sku ? sku?.master_uom?.master_uom_name : "";
  };

  return (
    <div>
      <div
        className="mt-4 sm:mx-6 lg:mx-8 space-y-6 relative h-screen"
        style={{ height: "calc(100vh - 110px)" }}
      >
        <div className="flex items-center space-x-4">
          <ul
            className="text-sm font-medium flex flex-nowrap overflow-x-scroll no-scrollbar border-b border-b-[#D9D9D9] space-x-12 grow"
            id="tabs-tabJustify"
            role="tablist"
          >
            <li className="text-left">
              <a
                className={`w-full block whitespace-nowrap text-sm leading-3 font-semibold py-2 border-b-2 border-[--bg-color-500] text-[--bg-color-500]`}
                id="users"
              >
                Product Details
              </a>
            </li>
          </ul>
          <button
            className="btn !bg-[--bg-color-500] !text-white cursor-pointer"
            type="button"
            onClick={() => formik.handleSubmit()}
          >
            {loader ? (
              <svg
                className="animate-spin w-5 h-45 fill-current shrink-0"
                viewBox="0 0 16 16"
              >
                <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
              </svg>
            ) : state ? (
              "Update Product"
            ) : (
              "Create Product"
            )}
          </button>
        </div>

        <div className="rounded-md bg-[--bg-color-50] p-4 space-y-6">
          <h4 className="text-[#535860] font-bold leading-[100%] text-base">
            Product Details
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between items-center space-x-4">
              <div className="w-full">
                <InputField
                  label="Product Name"
                  placeholder="Enter Product Name"
                  name="product_name"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("product_name", value);
                  }}
                  error={
                    Boolean(
                      formik.touched.product_name && formik.errors.product_name
                    )
                      ? formik.errors.product_name
                      : null
                  }
                  value={formik.values.product_name}
                  required
                />
              </div>
              <div className="w-full">
                <InputField
                  label="Product Number"
                  placeholder="Enter Product Number"
                  name="product_no"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("product_no", value);
                  }}
                  value={formik.values.product_no}
                />
              </div>
            </div>
            <div className="w-full">
              <InputField
                label="Product Description"
                placeholder="Enter Product Description"
                name="description"
                onBlur={formik.handleBlur}
                onChange={(e) => {
                  const value = sanitizeInput(e.target.value);
                  formik.setFieldValue("description", value);
                }}
                value={formik.values.description}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between items-center space-x-4">
              <div className="w-full">
                <InputField
                  label="Minimum Stock Level"
                  placeholder="Enter Minimum Stock Level"
                  name="min_qty"
                  onBlur={formik.handleBlur}
                  onChange={(e) => {
                    const value = sanitizeInput(e.target.value);
                    formik.setFieldValue("min_qty", value);
                  }}
                  error={
                    Boolean(formik.touched.min_qty && formik.errors.min_qty)
                      ? formik.errors.min_qty
                      : null
                  }
                  value={formik.values.min_qty}
                  required
                />
              </div>
              <div className="w-full">
                <label
                  className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                  htmlFor="card-city"
                >
                  Unit of Measurement (UOM)&nbsp;
                  <span className="text-rose-500">*</span>
                </label>
                <Select
                  className="basic-single text-sm unresetText"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      backgroundColor: "white",
                      borderColor: "lightgray",
                    }),
                    menuPortal: (base) => ({
                      ...base,
                      zIndex: 9999,
                    }),
                    placeholder: (provided, state) => ({
                      ...provided,
                      color: "#9297A2",
                      fontSize: "14px",
                    }),
                  }}
                  classNames={{
                    control: (style) => {
                      return `!border !shadow-sm ${
                        style.isFocused
                          ? "!border-[--bg-color-300]/50"
                          : "!border-slate-200 hover:!border-gray-300"
                      }`;
                    },
                    container: (style) => {
                      return "!h-[37.5px] !font-inter !text-sm";
                    },
                    menuList: (style) => {
                      return `!max-h-[200px]`;
                    },
                  }}
                  onChange={(UOM) => {
                    formik.setFieldValue("master_uom_id", UOM?.value);
                  }}
                  value={UOMOptions.find(
                    (element) => element.value === formik.values.master_uom_id
                  )}
                  options={UOMOptions}
                  placeholder="Select Unit of Measurement"
                />
                {Boolean(
                  formik.touched.master_uom_id && formik.errors.master_uom_id
                ) && (
                  <div className="text-xs text-rose-600 pt-1 pl-1">
                    {formik.errors.master_uom_id}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="rounded-md bg-[--bg-color-50] p-4 space-y-6 h-screen overflow-y-auto scrollbar scrollbar-w-2 scrollbar-thumb-rounded-full scrollbar-track-rounded-full scrollbar-thumb-gray-300 scrollbar-track-gray-100"
          style={{ height: "calc(100vh - 470px)" }}
        >
          <h4 className="text-[#535860] font-bold leading-[100%] text-base">
            Product Composition
          </h4>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate>
              <FieldArray
                name="sku"
                render={(arrayHelpers) => (
                  <div className="space-y-2">
                    {formik.values.sku.map((item, index) => {
                      return (
                        <div
                          key={index}
                          className="w-3/4 grid grid-cols-3 gap-2 relative"
                        >
                          <div className="w-full">
                            <label
                              className={`block text-sm font-medium mb-1 leading-3 tracking-tight`}
                            >
                              SKU&nbsp;
                              <span className="text-rose-500">*</span>
                            </label>
                            <Select
                              className="basic-single text-sm unresetText"
                              menuPlacement="top"
                              menuPortalTarget={document.body}
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  backgroundColor: "white",
                                  borderColor: "lightgray",
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                placeholder: (provided, state) => ({
                                  ...provided,
                                  color: "#9297A2",
                                  fontSize: "14px",
                                }),
                              }}
                              classNames={{
                                control: (style) => {
                                  return `!border !shadow-sm ${
                                    style.isFocused
                                      ? "!border-[--bg-color-300]/50"
                                      : "!border-slate-200 hover:!border-gray-300"
                                  }`;
                                },
                                container: (style) => {
                                  return "!h-[37.5px] !font-inter !text-sm";
                                },
                                menuList: (style) => {
                                  return `!max-h-[200px]`;
                                },
                              }}
                              onChange={(sku) => {
                                formik.setFieldValue(
                                  `sku[${index}].sku_master_id`,
                                  sku?.value
                                );
                              }}
                              value={
                                skuOptions.find(
                                  (item) =>
                                    item.value ===
                                    formik.values.sku[index].sku_master_id
                                )
                                  ? skuOptions.find(
                                      (item) =>
                                        item.value ===
                                        formik.values.sku[index].sku_master_id
                                    )
                                  : ""
                              }
                              options={skuOptions.filter((optionItem) => {
                                return !formik.values.sku.some(
                                  (el) => el?.sku_master_id === optionItem.value
                                );
                              })}
                              placeholder="Select SKU"
                            />
                            {Boolean(
                              formik?.touched?.sku?.[index]?.sku_master_id &&
                                formik?.errors?.sku?.[index]?.sku_master_id
                            ) && (
                              <div className="text-xs text-rose-600 pt-1 pl-1">
                                {formik.errors.sku[index].sku_master_id}
                              </div>
                            )}
                          </div>
                          <div className="w-full">
                            <InputField
                              label="Quantity per Product"
                              placeholder="Enter Quantity per Product"
                              name="qty"
                              onBlur={formik.handleBlur}
                              onChange={(e) => {
                                const value = sanitizeInput(e.target.value);
                                formik.setFieldValue(
                                  `sku[${index}].qty`,
                                  value
                                );
                              }}
                              error={
                                Boolean(
                                  formik?.touched?.sku?.[index]?.qty &&
                                    formik?.errors?.sku?.[index]?.qty
                                )
                                  ? formik.errors.sku[index].qty
                                  : null
                              }
                              value={
                                formik.values.sku[index].qty !== null && formik.values.sku[index].qty !== undefined
                                // formik.values.sku[index].qty
                                  ? formik.values.sku[index].qty
                                  : ""
                              }
                              required
                              suffix={getSkuUnit(
                                formik.values.sku[index].sku_master_id
                              )}
                            />
                          </div>
                          <div className="flex justify-start items-center">
                            {formik.values.sku.length > 1 && (
                              <span
                                title="Delete"
                                className="fa-solid fa-trash-can mt-6 text-rose-500 cursor-pointer"
                                onClick={() => arrayHelpers.remove(index)}
                              ></span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    <h6
                      className={`text-xs font-semibold leading-3 tracking-tight text-[--bg-color-500] cursor-pointer my-2`}
                      onClick={() => {
                        state
                          ? arrayHelpers.push({
                              sku_master_id: "",
                              qty: "",
                              product_master_id: state,
                            })
                          : arrayHelpers.push({
                              sku_master_id: "",
                              qty: "",
                            });
                      }}
                    >
                      + Add Item
                    </h6>
                  </div>
                )}
              />
            </Form>
          </FormikProvider>
        </div>

        <div className="absolute bottom-1">
          <button
            className="btn bg-white border-slate-200 hover:border-slate-300 text-slate-600 cursor-pointer"
            onClick={() => {
              dispatch({ type: RESET_SINGLE_PRODUCT_DATA });
              navigate("/product/overview");
              formik.resetForm();
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

CreateProduct.auth = {
  path: "/product/create",
  role: "super-admin admin",
};
export default CreateProduct;
